/**
 * User Permissions catalog — human-facing Access Control grid.
 *
 * Internal keys are existing Phase 2 technical capability identifiers.
 * Those identifiers are not shown in the Administrator UI.
 *
 * This catalog does not replace Module Access (none/read/write),
 * SmartPhrase authorization, or ROLE_PERMISSIONS enforcement.
 * Administrator is not a User Class.
 */

export const USER_CLASSES = [
  { id: "front_desk", label: "Front Desk" },
  { id: "clinical", label: "Clinical" },
  { id: "billing", label: "Billing" },
];

export const PERMISSION_GUIDE_HREF = "/docs/EMRbyMD_User_Permission_Guide.pdf";

export const PERMISSION_GROUPS = [
  {
    id: "clinical_documentation",
    number: 1,
    label: "Clinical Documentation",
    permissions: [
      {
        id: "clinical.document",
        label: "Manage Draft Notes",
        description: "Create and edit unsigned clinical note drafts.",
      },
      {
        id: "clinical.assess_plan",
        label: "Edit Assessment & Plan",
        description: "Modify the assessment and plan on the problem list.",
      },
      {
        id: "encounter.sign",
        label: "Sign Note",
        description: "Sign an encounter or clinical note.",
      },
      {
        id: "encounter.amend",
        label: "Amend Signed Note",
        description: "Amend a signed encounter or clinical note.",
      },
    ],
  },
  {
    id: "patient_encounter",
    number: 2,
    label: "Patient & Encounter",
    permissions: [
      {
        id: "patient.register",
        label: "Register Patient",
        description: "Create a new patient record.",
      },
      {
        id: "patient.deactivate",
        label: "Deactivate Patient",
        description: "Deactivate a patient chart.",
      },
      {
        id: "intake.record",
        label: "Record Intake",
        description: "Record patient intake information.",
      },
      {
        id: "triage.record",
        label: "Record Triage",
        description: "Record triage information.",
      },
      {
        id: "encounter.manage",
        label: "Manage Encounters",
        description: "Manage the encounter lifecycle.",
      },
      {
        id: "encounter.close",
        label: "Close Encounter",
        description: "Close an encounter.",
      },
      {
        id: "encounter.reopen",
        label: "Reopen Encounter",
        description: "Reopen a closed encounter.",
      },
    ],
  },
  {
    id: "scheduling",
    number: 3,
    label: "Scheduling",
    permissions: [
      {
        id: "scheduling.view",
        label: "View Appointments",
        description: "View scheduling data.",
      },
      {
        id: "scheduling.manage",
        label: "Manage Appointments",
        description: "Create, update, or cancel appointments.",
      },
    ],
  },
  {
    id: "administration_billing",
    number: 4,
    label: "Administration & Billing",
    permissions: [
      {
        id: "admin.org_location",
        label: "Manage Organizations & Locations",
        description: "Manage organizations and locations.",
      },
      {
        id: "admin.users",
        label: "Manage Users & Access",
        description: "Administer users and Access Control configuration.",
      },
      {
        id: "terminology.admin",
        label: "Manage Clinical Terminology",
        description: "Maintain clinical terminology and master lists.",
      },
      {
        id: "system.config",
        label: "System Administration",
        description: "Perform system configuration and restricted administrative functions.",
      },
      {
        id: "billing.claims",
        label: "Manage Claims",
        description: "Access and manage billing claims.",
      },
    ],
  },
];

export const PERMISSION_IDS = PERMISSION_GROUPS.flatMap((group) =>
  group.permissions.map((item) => item.id),
);

/** Recommended EMRbyMD Preferred Preferences. */
export const PREFERRED_GRANTS = {
  "clinical.document": { front_desk: false, clinical: true, billing: false },
  "clinical.assess_plan": { front_desk: false, clinical: true, billing: false },
  "encounter.sign": { front_desk: false, clinical: true, billing: false },
  "encounter.amend": { front_desk: false, clinical: true, billing: false },
  "patient.register": { front_desk: true, clinical: true, billing: false },
  "patient.deactivate": { front_desk: false, clinical: true, billing: false },
  "intake.record": { front_desk: true, clinical: true, billing: false },
  "triage.record": { front_desk: false, clinical: true, billing: false },
  "encounter.manage": { front_desk: false, clinical: true, billing: false },
  "encounter.close": { front_desk: false, clinical: true, billing: false },
  "encounter.reopen": { front_desk: false, clinical: true, billing: false },
  "scheduling.view": { front_desk: true, clinical: true, billing: true },
  "scheduling.manage": { front_desk: true, clinical: true, billing: false },
  "admin.org_location": { front_desk: false, clinical: false, billing: false },
  "admin.users": { front_desk: false, clinical: false, billing: false },
  "terminology.admin": { front_desk: false, clinical: false, billing: false },
  "system.config": { front_desk: false, clinical: false, billing: false },
  "billing.claims": { front_desk: false, clinical: false, billing: true },
};

export function cloneGrants(source) {
  const out = {};
  PERMISSION_IDS.forEach((id) => {
    const row = source?.[id] || PREFERRED_GRANTS[id];
    out[id] = {
      front_desk: Boolean(row?.front_desk),
      clinical: Boolean(row?.clinical),
      billing: Boolean(row?.billing),
    };
  });
  return out;
}

export function preferredGrants() {
  return cloneGrants(PREFERRED_GRANTS);
}

export function grantsEqual(left, right) {
  return JSON.stringify(cloneGrants(left)) === JSON.stringify(cloneGrants(right));
}

export function setGrant(grants, permissionId, userClass, allowed) {
  return {
    ...grants,
    [permissionId]: {
      ...grants[permissionId],
      [userClass]: Boolean(allowed),
    },
  };
}

export function permissionTestId(permissionId, userClass) {
  return `ac-perm-${permissionId.replace(/\./g, "-")}-${userClass}`;
}
